import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Modal } from '../components/Modal';
import { verifyOtp, resendOtp } from '../services/api';
import { getOtpErrorMessage } from '../utils/errorMessages';
import { ROUTES } from '../constants';
import { trackLead, trackOtpVerified } from '../utils/analytics';

export const VerifyOtp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const email = location.state?.email || '';
  const password = location.state?.password || '';
  const name = location.state?.name || '';

  useEffect(() => {
    if (!email || !password || !name) {
      navigate(ROUTES.REGISTER);
    }
  }, [email, password, name, navigate]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value[0];
    }

    if (!/^\d*$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newOtp.every(digit => digit !== '') && newOtp.join('').length === 6) {
      handleVerifyOtp(newOtp.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    if (!/^\d{6}$/.test(pastedData)) {
      setError('Please paste a valid 6-digit OTP');
      return;
    }

    const newOtp = pastedData.split('');
    setOtp(newOtp);
    inputRefs.current[5]?.focus();

    handleVerifyOtp(pastedData);
  };

  const handleVerifyOtp = async (otpValue: string) => {
    setLoading(true);
    setError('');

    const response = await verifyOtp(email, otpValue);

    if (response.success && response.data) {
      trackLead({
        content_name: 'Email Verification',
      });
      trackOtpVerified({
        email: email,
        verification_method: 'email',
      });
      setShowSuccessModal(true);
    } else {
      setError(getOtpErrorMessage(response.error));
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }

    setLoading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join('');

    if (otpValue.length !== 6) {
      setError('Please enter a complete 6-digit OTP');
      return;
    }

    handleVerifyOtp(otpValue);
  };

  const handleResendOtp = async () => {
    if (resendCountdown > 0) return;

    setResendLoading(true);
    setError('');
    setSuccessMessage('');

    const response = await resendOtp(email, password, name);

    if (response.success) {
      setSuccessMessage('OTP has been resent to your email');
      setResendCountdown(60);
      setTimeout(() => setSuccessMessage(''), 3000);
    } else {
      setError(getOtpErrorMessage(response.error));
    }

    setResendLoading(false);
  };

  const handleModalClose = () => {
    navigate(ROUTES.LOGIN);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-3 sm:p-4">
      <Card className="w-full max-w-md">
        <button
          onClick={() => navigate(ROUTES.REGISTER)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4 sm:mb-6 p-1 -ml-1"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span className="text-sm sm:text-base">Back</span>
        </button>

        <div className="text-center mb-6 sm:mb-8">
          <Logo size="md" className="justify-center mb-3 sm:mb-4" />
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Mail className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Verify Your Email
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            We've sent a 6-digit code to
          </p>
          <p className="text-sm sm:text-base font-medium text-gray-900 mt-1">
            {email}
          </p>
        </div>

        {successMessage && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg text-green-600 text-xs sm:text-sm text-center">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs sm:text-sm text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
              Enter Verification Code
            </label>
            <div className="flex gap-2 justify-center" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-12 sm:w-14 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-200 transition-all"
                  disabled={loading}
                  autoFocus={index === 0}
                />
              ))}
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            isLoading={loading}
            disabled={otp.join('').length !== 6}
          >
            Verify Email
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 mb-3">
            Didn't receive the code?
          </p>
          <Button
            variant="ghost"
            onClick={handleResendOtp}
            disabled={resendCountdown > 0 || resendLoading}
            isLoading={resendLoading}
            className="w-full"
          >
            {resendCountdown > 0
              ? `Resend code in ${resendCountdown}s`
              : 'Resend Code'}
          </Button>
        </div>
      </Card>

      <Modal
        isOpen={showSuccessModal}
        onClose={handleModalClose}
        title="Verification Successful!"
        message="Your email has been verified successfully. Click below to continue to the login page."
        variant="success"
        actionLabel="Go to Login"
        onAction={handleModalClose}
        showCloseButton={false}
      />
    </div>
  );
};
